/* The review modal — a focused read with a buy box, opened from "Read more".
 *
 * PROGRESSIVE ENHANCEMENT, and the shape of this file follows from it. The card's "Read more" is a
 * <label> driving a checkbox, and with no JavaScript that still works: the full review is already in
 * the card and the checkbox swaps which paragraph shows. This script INTERCEPTS that label and opens
 * the dialog instead. Nothing here is required for the reviews to be readable — which is the whole
 * reason the section renders its text server-side in the first place.
 *
 * ONE DIALOG, filled from the clicked card. The cards are rendered twice for the drift loop, so a
 * dialog per card would mean twenty-four of them; and duplicating the review text into a dialog
 * would put every review on the page twice, which is exactly the duplication the server-rendered
 * approach was meant to avoid.
 *
 * ONE MODAL, NOT TWO. Full Leaf opens a review overlay and then a second product overlay to pick a
 * size. The size buttons live in this one instead: same job, one dialog, and no nested-dialog focus
 * problem. It also matters more here than there — the sizes are 50g and 100g, and a blind "quick
 * add" would be guessing the only choice the customer actually cares about.
 */
(() => {
  const section = document.currentScript?.closest('.hr') || document.querySelector('.hr');
  if (!section) return;
  const modal = section.querySelector('.hr__modal');
  if (!modal || typeof modal.showModal !== 'function') return; /* no <dialog>: labels keep working */

  const $ = (s) => modal.querySelector(s);
  const thumb = $('.hr__modal-thumb');
  const product = $('.hr__modal-product');
  const stars = $('.hr__modal-stars');
  const title = $('.hr__modal-title');
  const body = $('.hr__modal-body');
  const by = $('.hr__modal-by');
  const sizes = $('.hr__sizes');
  const addBtn = $('.hr__add');
  const view = $('.hr__view');
  const note = $('.hr__buy-note');

  let chosen = null;

  const money = (n) => `$${n.toFixed(2).replace(/\.00$/, '')}`;

  const STAR = 'M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8L10 14.9 4.8 17.6l1-5.8L1.6 7.7l5.8-.8z';
  function drawStars(rating) {
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${rating} out of 5 stars`);
    stars.innerHTML = Array.from({ length: 5 }, (_, i) =>
      `<svg class="hr__star${i + 1 > rating ? ' is-empty' : ''}" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="${STAR}"/></svg>`
    ).join('');
  }

  function pickSize(v, btn) {
    chosen = v;
    sizes.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    addBtn.disabled = false;
    addBtn.textContent = `Add to cart · ${money(v.price)}`;
    note.textContent = '';
  }

  function buildSizes(variants) {
    sizes.innerHTML = '';
    chosen = null;
    note.textContent = '';

    const sellable = variants.filter((v) => v.available);

    /* Every size sold out is a real case in this catalogue, not an edge one — several of the
       reviewed teas have both sizes out. Say so plainly and leave "View product" as the way on,
       rather than offering a button that can only fail. */
    if (!sellable.length) {
      addBtn.hidden = true;
      sizes.hidden = true;
      note.textContent = variants.length ? 'Out of stock right now.' : '';
      note.className = 'hr__buy-note is-quiet';
      return;
    }

    addBtn.hidden = false;
    addBtn.disabled = true;
    addBtn.textContent = 'Add to cart';

    /* A single size needs no choosing — pre-select it and hide the row rather than showing a group
       of one, which reads as a decision the customer does not have. */
    if (variants.length === 1) {
      sizes.hidden = true;
      pickSize(variants[0], null);
      return;
    }

    sizes.hidden = false;
    for (const v of variants) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hr__size';
      b.setAttribute('aria-pressed', 'false');
      b.innerHTML = `<strong>${v.title}</strong><span>${money(v.price)}</span>`;
      if (!v.available) {
        b.disabled = true;
        b.classList.add('is-out');
        b.title = 'Out of stock';
        b.innerHTML += '<em>Out</em>';
      } else {
        b.addEventListener('click', () => pickSize(v, b));
      }
      sizes.appendChild(b);
    }
    /* Pre-select the first size that can actually be bought, so Add to cart is live immediately.
       Nothing is guessed away from the customer: the row stays on screen and the choice is one tap
       from being changed. */
    const first = sizes.querySelector('button:not([disabled])');
    if (first) first.click();
  }

  function open(card) {
    const d = card.dataset;
    const full = card.querySelector('.hr__body--full') || card.querySelector('.hr__body');

    if (d.image) { thumb.src = d.image; thumb.hidden = false; } else { thumb.hidden = true; }
    product.textContent = d.product;
    product.href = d.url;
    view.href = d.url;
    drawStars(Number(d.rating));

    const t = card.querySelector('.hr__quote-title');
    title.textContent = t ? t.textContent : '';
    title.hidden = !t;

    body.textContent = full ? full.textContent : '';
    by.innerHTML = `— ${d.reviewer}` + (d.verified === 'true' ? ' <span class="hr__verified">Verified</span>' : '');

    let variants = [];
    try { variants = JSON.parse(d.variants || '[]'); } catch { variants = []; }
    buildSizes(variants);

    modal.showModal();
  }

  /* Intercept the label. preventDefault stops the checkbox toggling, so the card behind the modal is
     not left expanded when it closes. */
  section.addEventListener('click', (e) => {
    const label = e.target.closest('.hr__more');
    if (!label) return;
    const card = label.closest('.hr__card');
    if (!card) return;
    e.preventDefault();
    open(card);
  });

  $('.hr__close').addEventListener('click', () => modal.close());

  /* Clicking the backdrop closes. The dialog element itself fills the viewport, so a click whose
     target IS the dialog (rather than anything inside .hr__modal-inner) landed on the backdrop. */
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.close(); });

  /* Dawn's cart surfaces. `cart-drawer` is the one this theme uses; `cart-notification` is the
     fallback when the drawer is switched off in theme settings. */
  const cartUI = () => document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  addBtn.addEventListener('click', async () => {
    if (!chosen) return;
    addBtn.disabled = true;
    const was = addBtn.textContent;
    addBtn.textContent = 'Adding…';
    note.textContent = '';
    try {
      /* ASK FOR THE RENDERED SECTIONS BACK. This is how Dawn's own product-form.js adds to cart:
         Shopify re-renders the named sections server-side and returns their HTML in the response,
         and handing that to renderContents() updates the drawer AND the header count in one go.

         The first version of this published a `cartUpdate` event instead and nothing happened —
         the item went into the cart but no surface knew. Two reasons it failed, and the first is
         the instructive one: PUB_SUB_EVENTS is a top-level `const` in assets/constants.js, so it
         is a global BINDING but not a property of `window`. The guard `window.PUB_SUB_EVENTS &&
         …` was therefore always false and the publish never ran. Even fixed, an event only works
         if something is listening; asking for the sections does not depend on that. */
      const ui = cartUI();
      const sections = ui?.getSectionsToRender?.().map((s) => s.id);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: chosen.id,
          quantity: 1,
          ...(sections ? { sections, sections_url: window.location.pathname } : {}),
        }),
      });
      /* Shopify answers a refused add — out of stock, or more than the shop holds — with a 4xx and a
         JSON description. Without reading it the button would say "Added" for something that was
         not. The showroom learned this the hard way; same check here. */
      if (!res.ok) {
        const why = await res.json().catch(() => ({}));
        note.textContent = why.description || why.message || 'That is not available right now.';
        note.className = 'hr__buy-note is-bad';
        addBtn.textContent = was;
        addBtn.disabled = false;
        return;
      }
      const json = await res.json();
      note.textContent = 'Added to your cart.';
      note.className = 'hr__buy-note is-good';
      addBtn.textContent = 'Added';

      /* Tell anything that is listening — the cart drawer's buy box subscribes to this. Bare
         identifiers with a typeof guard, NOT `window.PUB_SUB_EVENTS`: constants.js declares it with
         `const` at the top level of a classic script, which creates a global binding that never
         becomes a property of window. That distinction is what silently broke the first version. */
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'home-reviews', productVariantId: chosen.id, cartData: json });
        }
      } catch { /* a listener throwing must not make a successful add look failed */ }

      /* Close before rendering. renderContents() opens the drawer, and a drawer sliding up UNDER a
         top-layer <dialog> is invisible — which is precisely how this felt broken: the item was in
         the cart, but every surface that could have said so was behind the modal. */
      if (ui?.renderContents && json.sections) {
        modal.close();
        ui.renderContents(json);
      }
    } catch {
      note.textContent = 'That did not go in — try again.';
      note.className = 'hr__buy-note is-bad';
      addBtn.textContent = was;
      addBtn.disabled = false;
    }
  });
})();
